import {
  useQuery as useTanStackQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

/**
 * Re-export React Query hooks with custom configurations
 * This allows for consistent usage patterns across the application
 */

// Re-export the main hooks
export { useTanStackQuery as useQuery, useMutation, useQueryClient };

/**
 * Query keys factory for consistent key management
 * This helps prevent typos and provides better IntelliSense
 */
export const queryKeys = {
  // Clinic-related queries
  clinic: (slug: string) => ['clinic', slug] as const,
  clinicServices: (slug: string) => ['clinic', slug, 'services'] as const,
  clinicProviders: (slug: string, serviceId: string) =>
    ['clinic', slug, 'providers', serviceId] as const,
  clinicConfig: (slug: string) => ['clinic', slug, 'config'] as const,
  clinicTestimonials: (slug: string) =>
    ['clinic', slug, 'testimonials'] as const,

  // Patient-related queries
  patient: (id: string) => ['patient', id] as const,
  patientAppointments: (patientId: string) =>
    ['patient', patientId, 'appointments'] as const,

  // Appointment-related queries
  appointments: () => ['appointments'] as const,
  appointment: (id: string) => ['appointment', id] as const,
  availableSlots: (providerId: string, date: string) =>
    ['availableSlots', providerId, date] as const,

  // Service-related queries
  services: () => ['services'] as const,
  service: (id: string) => ['service', id] as const,
} as const;

/**
 * Custom hook for invalidating multiple related queries
 * Useful for cache management after mutations
 */
export const useInvalidateQueries = () => {
  const queryClient = useQueryClient();

  const invalidateClinicData = (clinicSlug: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.clinic(clinicSlug) });
    queryClient.invalidateQueries({
      queryKey: queryKeys.clinicServices(clinicSlug),
    });
    // Invalidate all providers for the clinic regardless of service
    queryClient.invalidateQueries({
      queryKey: ['clinic', clinicSlug, 'providers'],
    });
  };

  const invalidatePatientData = (patientId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.patient(patientId) });
    queryClient.invalidateQueries({
      queryKey: queryKeys.patientAppointments(patientId),
    });
  };

  const invalidateAppointments = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.appointments() });
  };

  return {
    invalidateClinicData,
    invalidatePatientData,
    invalidateAppointments,
  };
};
